import React, { useState } from "react";
import { IndianRupee, Plus, X } from "lucide-react";
import { AppButton, Checkbox, FormField, IconButton, Input, NativeSelect, Radio, Textarea, cn } from "../../ui";

const toDateInput = (d) => (d ? String(d).slice(0, 10) : "");
const listText = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr || "");
const num = (v) => (v == null ? "" : String(v));

/** The report as the form holds it, from the evaluation as the server holds it. */
export function formFromEvaluation(ev, { counsellorName = "" } = {}) {
  const r = ev?.recommendation || {};
  const s = ev?.salary || {};
  const p = ev?.program || {};
  return {
    domains: r.domains || [],
    reason: r.reason || "",
    comments: r.comments || "",
    expectedCtcMin: num(s.expectedCtcMin),
    expectedCtcMax: num(s.expectedCtcMax),
    recommendedMinCtc: num(s.recommendedMinCtc),
    potentialCtcMin: num(s.potentialCtcMin),
    potentialCtcMax: num(s.potentialCtcMax),
    preferredLocations: listText(s.preferredLocations),
    alternativeLocations: listText(s.alternativeLocations),
    workModes: s.workModes || [],
    noticePeriod: s.noticePeriod || "",
    recommendedProgram: p.recommendedProgram || "",
    trainingRequired: p.trainingRequired === true ? "yes" : p.trainingRequired === false ? "no" : "",
    placementAssistancePlan: p.placementAssistancePlan || "",
    programCharges: num(p.programCharges),
    conditions: p.conditions || "",
    counsellorName: p.counsellorName || counsellorName,
    evaluationDate: toDateInput(p.evaluationDate) || new Date().toISOString().slice(0, 10),
  };
}

/** The body PUT /evaluation takes, from the form. */
export function bodyFromForm(f) {
  return {
    recommendation: { domains: f.domains, reason: f.reason, comments: f.comments },
    salary: {
      expectedCtcMin: f.expectedCtcMin,
      expectedCtcMax: f.expectedCtcMax,
      recommendedMinCtc: f.recommendedMinCtc,
      potentialCtcMin: f.potentialCtcMin,
      potentialCtcMax: f.potentialCtcMax,
      preferredLocations: f.preferredLocations,
      alternativeLocations: f.alternativeLocations,
      workModes: f.workModes,
      noticePeriod: f.noticePeriod,
    },
    program: {
      recommendedProgram: f.recommendedProgram,
      trainingRequired: f.trainingRequired || null,
      placementAssistancePlan: f.placementAssistancePlan,
      programCharges: f.programCharges,
      conditions: f.conditions,
      counsellorName: f.counsellorName,
      evaluationDate: f.evaluationDate,
    },
  };
}

/** A list of chips with a picker beside it: the suggested domains, or one typed in. */
const DomainsInput = ({ value = [], onChange, suggestions = [], disabled }) => {
  const [typed, setTyped] = useState("");
  const add = (domain) => {
    const d = String(domain || "").trim();
    if (!d || value.some((v) => v.toLowerCase() === d.toLowerCase()) || value.length >= 10) return;
    onChange([...value, d]);
    setTyped("");
  };
  const left = suggestions.filter((s) => !value.includes(s));
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {value.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-1.5 tw-p-0">
          {value.map((d) => (
            <li key={d} className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-primary-soft tw-py-0.5 tw-pl-2.5 tw-pr-1 tw-text-xs tw-font-medium tw-text-primary-soft-fg">
              {d}
              {!disabled && <IconButton label={`Remove ${d}`} icon={X} size="xs" variant="ghost" onClick={() => onChange(value.filter((v) => v !== d))} />}
            </li>
          ))}
        </ul>
      )}
      {!disabled && (
        <div className="tw-grid tw-gap-2 min-[576px]:tw-grid-cols-[1fr_1fr_auto]">
          <NativeSelect value="" onChange={(e) => add(e.target.value)} aria-label="Add a suggested domain">
            <option value="">Pick a domain…</option>
            {left.map((s) => <option key={s} value={s}>{s}</option>)}
          </NativeSelect>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(typed); } }}
            placeholder="or type one"
            aria-label="Type a domain"
          />
          <AppButton type="button" variant="outline" onClick={() => add(typed)} disabled={!typed.trim()}>
            <Plus size={16} /> Add
          </AppButton>
        </div>
      )}
    </div>
  );
};

const Block = ({ title, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-3">
    <h4 className="tw-m-0 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-muted">{title}</h4>
    {children}
  </div>
);

/** Two bounded numbers that read as one range. */
const RangeField = ({ label, min, max, onMin, onMax, disabled }) => (
  <FormField label={label} hint="Lakhs per annum">
    <div className="tw-grid tw-grid-cols-[1fr_auto_1fr] tw-items-center tw-gap-1.5">
      <Input inputMode="decimal" value={min} onChange={(e) => onMin(e.target.value)} placeholder="min" aria-label={`${label} from`} readOnly={disabled} />
      <span className="tw-text-xs tw-text-content-subtle">to</span>
      <Input inputMode="decimal" value={max} onChange={(e) => onMax(e.target.value)} placeholder="max" aria-label={`${label} to`} readOnly={disabled} />
    </div>
  </FormField>
);

/**
 * EvaluationForm — the office's report on a contract student, kept short: the
 * recommendation, the indicative figures and the programme, each a compact
 * grid. Value-first (`value` from formFromEvaluation, `onChange(next)`);
 * `readOnly` once the agreement is sent.
 */
const EvaluationForm = ({ value, onChange, options, readOnly = false }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const toggleMode = (key) => set({
    workModes: value.workModes.includes(key) ? value.workModes.filter((m) => m !== key) : [...value.workModes, key],
  });

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <Block title="Recommendation">
        <FormField label="Recommended job domain" hint="One or more">
          <DomainsInput value={value.domains} onChange={(domains) => set({ domains })} suggestions={options.suggestedDomains} disabled={readOnly} />
        </FormField>
        <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
          <FormField label="Reason">
            <Textarea rows={2} value={value.reason} onChange={(e) => set({ reason: e.target.value })} placeholder="Why this domain fits" readOnly={readOnly} />
          </FormField>
          <FormField label="Eligibility gaps or additional requirements">
            <Textarea rows={2} value={value.comments} onChange={(e) => set({ comments: e.target.value })} placeholder="What is missing, or needed first" readOnly={readOnly} />
          </FormField>
        </div>
      </Block>

      <Block title="Indicative salary & job preferences">
        <div className={cn("tw-grid tw-gap-3 min-[576px]:tw-grid-cols-2 min-[1200px]:tw-grid-cols-3")}>
          <RangeField label="Expected CTC range" min={value.expectedCtcMin} max={value.expectedCtcMax} onMin={(v) => set({ expectedCtcMin: v })} onMax={(v) => set({ expectedCtcMax: v })} disabled={readOnly} />
          <FormField label="Recommended minimum CTC" hint="Lakhs per annum">
            <Input inputMode="decimal" value={value.recommendedMinCtc} onChange={(e) => set({ recommendedMinCtc: e.target.value })} placeholder="e.g. 3.5" readOnly={readOnly} />
          </FormField>
          <RangeField label="Potential CTC range" min={value.potentialCtcMin} max={value.potentialCtcMax} onMin={(v) => set({ potentialCtcMin: v })} onMax={(v) => set({ potentialCtcMax: v })} disabled={readOnly} />
          <FormField label="Preferred job locations" hint="Comma-separated">
            <Input value={value.preferredLocations} onChange={(e) => set({ preferredLocations: e.target.value })} placeholder="Hyderabad, Bengaluru" readOnly={readOnly} />
          </FormField>
          <FormField label="Alternative job locations" hint="Comma-separated">
            <Input value={value.alternativeLocations} onChange={(e) => set({ alternativeLocations: e.target.value })} placeholder="Pune, Chennai" readOnly={readOnly} />
          </FormField>
          <FormField label="Notice period / joining availability">
            <Input value={value.noticePeriod} onChange={(e) => set({ noticePeriod: e.target.value })} placeholder="e.g. Immediate, 30 days" readOnly={readOnly} />
          </FormField>
        </div>
        <fieldset className="tw-m-0 tw-flex tw-flex-col tw-gap-1.5 tw-border-0 tw-p-0">
          <legend className="tw-mb-1 tw-p-0 tw-text-[0.8125rem] tw-font-semibold tw-text-content">Work mode preference</legend>
          <div className="tw-flex tw-flex-wrap tw-gap-4">
            {(options.workModes || []).map((m) => (
              <Checkbox key={m.key} label={m.label} checked={value.workModes.includes(m.key)} onChange={() => toggleMode(m.key)} disabled={readOnly} />
            ))}
          </div>
        </fieldset>
      </Block>

      <Block title="Placement programme">
        <div className="tw-grid tw-gap-3 min-[576px]:tw-grid-cols-2">
          <FormField label="Recommended training / placement programme">
            <Input value={value.recommendedProgram} onChange={(e) => set({ recommendedProgram: e.target.value })} placeholder="e.g. Full Stack Placement Programme" readOnly={readOnly} />
          </FormField>
          <fieldset className="tw-m-0 tw-flex tw-flex-col tw-gap-1.5 tw-border-0 tw-p-0">
            <legend className="tw-mb-1 tw-p-0 tw-text-[0.8125rem] tw-font-semibold tw-text-content">Training required</legend>
            <div className="tw-flex tw-gap-4 tw-pt-1.5">
              <Radio name="trainingRequired" value="yes" label="Yes" checked={value.trainingRequired === "yes"} onChange={() => set({ trainingRequired: "yes" })} disabled={readOnly} />
              <Radio name="trainingRequired" value="no" label="No" checked={value.trainingRequired === "no"} onChange={() => set({ trainingRequired: "no" })} disabled={readOnly} />
            </div>
          </fieldset>
          <FormField label="Placement assistance plan" className="min-[576px]:tw-col-span-2">
            <Textarea rows={2} value={value.placementAssistancePlan} onChange={(e) => set({ placementAssistancePlan: e.target.value })} placeholder="What the institute will do: mock interviews, referrals, drives…" readOnly={readOnly} />
          </FormField>
          <FormField label="Applicable programme charges">
            <Input inputMode="numeric" leading={<IndianRupee size={14} aria-hidden="true" />} value={value.programCharges} onChange={(e) => set({ programCharges: e.target.value })} placeholder="0" readOnly={readOnly} />
          </FormField>
          <FormField label="Additional conditions or eligibility requirements">
            <Input value={value.conditions} onChange={(e) => set({ conditions: e.target.value })} placeholder="e.g. Complete the aptitude module" readOnly={readOnly} />
          </FormField>
          <FormField label="Assigned counsellor / evaluator">
            <Input value={value.counsellorName} onChange={(e) => set({ counsellorName: e.target.value })} readOnly={readOnly} />
          </FormField>
          <FormField label="Evaluation date">
            <Input type="date" value={value.evaluationDate} onChange={(e) => set({ evaluationDate: e.target.value })} readOnly={readOnly} />
          </FormField>
        </div>
      </Block>
    </div>
  );
};

export default EvaluationForm;
